import { Fragment, useEffect, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

import Tooltip from "./tooltip";

interface Props {
  options: {
    id: string | number;
    name: string;
    value: string;
    disabled?: boolean;
  }[];
  error?: boolean;
  errorMessage?: string;
  defaultValue?: any;
  setterFunction?: (value: any) => void;
  label?: string;
  labelSize?: string;
  optional?: boolean;
  infoicon?: boolean;
  infoMessage?: string;
  name?: string;
  register?: (...args: any[]) => void;
  testId?: string;
}

function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}

const SelectCustom = ({
  options,
  error,
  errorMessage,
  defaultValue,
  setterFunction,
  label,
  labelSize,
  optional,
  infoicon,
  infoMessage,
  name,
  register,
  testId,
}: Props) => {
  const [selected, setSelected] = useState<any>({});

  useEffect(() => {
    setSelected(defaultValue || options[0]);
  }, [options, defaultValue]);

  return (
    <div className="min-w-28">
      <Listbox
        value={selected}
        onChange={(el) => {
          if (el?.disabled === true) {
            return;
          }
          setSelected(el);
          if (setterFunction) {
            setterFunction(el);
          }
        }}
        data-testid={testId}
      >
        {({ open }) => (
          <div className="flex flex-col items-start space-y-1 w-full">
            {label && (
              <label
                className={`text-secondary flex mb-1 font-medium ${labelSize}`}
              >
                {label}
                {optional && (
                  <span className="text-sm ps-2 font-thin">(Optional)</span>
                )}
                {infoicon && (
                  <Tooltip
                    message={` ${infoMessage}`}
                    position="left-3  w-[7rem] -mt-3 ms-2"
                  >
                    {" "}
                    <InformationCircleIcon
                      className="w-6 h-6 ps-1"
                      data-testid={`${testId}InfoIcon`}
                    />{" "}
                  </Tooltip>
                )}
              </label>
            )}
            <div className="relative  w-full">
              <Listbox.Button
                className={`relative w-full cursor-default rounded-md bg-bkg py-1.5 pl-3 pr-10 text-left text-secondary shadow-sm ring-1 ring-inset ${
                  error ? "ring-red-500" : "ring-border focus:ring-primary"
                } focus:outline-none focus:ring-1  sm:text-sm sm:leading-6`}
              >
                {label == "Domain" || label?.toLowerCase() == "subcategory" ? (
                  <span
                    className="block truncate"
                    data-twe-ripple-init
                    data-twe-ripple-color="light"
                    title={selected?.name}
                  >
                    {selected?.name}
                  </span>
                ) : (
                  <span className="block truncate">{selected?.name}</span>
                )}
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon
                    className="h-5 w-5 text-secondary/50"
                    aria-hidden="true"
                  />
                </span>
              </Listbox.Button>

              <Transition
                show={open}
                as={undefined}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-bkg py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {options?.map((option) => (
                    <Listbox.Option
                      key={option.id}
                      className={({ active }) =>
                        classNames(
                          active
                            ? `bg-primary text-white dark:text-black odd:bg-primary }`
                            : `text-secondary hover:bg-primary odd:bg-border/20 hover:text-white`,
                          ` relative cursor-default select-none py-2 pl-3 pr-9 z-20 ${
                            option.disabled
                              ? " cursor-not-allowed hover:bg-bkg hover:text-black"
                              : ""
                          }`
                        )
                      }
                      value={option}
                      {...(register ? register(name) : {})}
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={classNames(
                              selected ? "font-semibold" : "font-normal",
                              "block"
                            )}
                          >
                            {option.name}
                          </span>

                          {selected ? (
                            <span
                              className={classNames(
                                active ? "text-white" : "text-primary",
                                "absolute inset-y-0 right-0 flex items-center pr-4"
                              )}
                            >
                              <CheckIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
            {error && errorMessage && (
              <p className="text-sm text-red-500">{errorMessage}</p>
            )}
          </div>
        )}
      </Listbox>
    </div>
  );
};

export default SelectCustom;
